import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  RadioButton,
  Select,
  Checkbox,
  Banner,
  Badge,
  Tag,
  Divider,
  Box,
} from '@shopify/polaris';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import { useStore } from '../../providers/StoreProvider';
import { LocationPickerModal } from '../../components/LocationPickerModal';
import {
  CatalogPickerModal,
  type CatalogItem,
} from '../../components/CatalogPickerModal';
import { SelectedProductRow } from '../../components/SelectedProductRow';
import {
  CampaignNameEditor,
  campaignPageLabel,
} from '../../components/CampaignNameEditor';
import {
  generateFreebieCode,
  resolveUniqueDiscountCode,
} from '../../lib/discountCode';
import {
  CustomerSegmentField,
  segmentIdsFromValue,
  segmentNamesFromValue,
  segmentValueFromEligibility,
} from '../../components/CustomerSegmentField';
import {
  CampaignScheduleFields,
  defaultScheduleValue,
  scheduleFromIso,
  scheduleToIso,
  type CampaignScheduleValue,
} from '../../components/CampaignScheduleFields';
import { DEFAULT_POPUP_DESIGN } from '../widgets/popupDefaults';
import { PopupPreview } from '../widgets/PopupPreview';
import { DEFAULT_CONGRATS_DESIGN } from '../widgets/congratsDefaults';
import { CongratsPreview } from '../widgets/CongratsPreview';
import {
  DEFAULT_PROMO_TITLE,
  DEFAULT_PROMO_SUBTITLE_QTY,
  DEFAULT_PROMO_SUBTITLE_SPEND,
  offeredAmount,
  parseMoneyAmount,
} from '../widgets/promoDefaults';
import { CampaignPromoWidgetCard } from '../../components/CampaignPromoWidgetCard';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import { t } from '../../i18n';

type MainStep = 1 | 2;
type SideSection = 'buys' | 'gets' | 'stacking' | 'advanced';
/** spend = cart value threshold · quantity = item count threshold */
export type FreeGiftWizardMode = 'spend' | 'quantity';

type AppliesTo = 'all' | 'collections' | 'tags' | 'products';

/** One row per product; variants nested (Shopify-style “2 of 8 variants”) */
interface GiftProduct {
  /** Product id (group key) */
  id: string;
  title: string;
  imageUrl?: string | null;
  /** Free units of this product the shopper gets (not per-variant qty) */
  quantity?: number;
  productId?: string;
  /** @deprecated single pin — prefer selectedVariants / allowedVariantIds */
  variantId?: string | null;
  /** Allowed variants for popup; empty = whole product (any variant) */
  selectedVariants?: Array<{
    id: string;
    title: string;
    price?: string | null;
    available?: boolean;
  }>;
  /** Total variants on the product (for “2 of 8” label) */
  totalVariants?: number;
  /** Subtitle for SelectedProductRow (computed) */
  subtitle?: string | null;
  /** True when selected variants (or product) are unavailable for sale */
  outOfStock?: boolean;
}

function stripShopifyId(id: string | null | undefined): string {
  if (id == null) return '';
  const s = String(id).trim();
  if (!s) return '';
  return (
    s
      .replace(/^gid:\/\/shopify\/(Product|ProductVariant|Collection)\//, '')
      .replace(/\D/g, '') || s
  );
}

function productTitleFromCatalog(item: CatalogItem): string {
  if (item.productTitle && String(item.productTitle).trim()) {
    return String(item.productTitle).trim();
  }
  const t = String(item.title || '');
  // CatalogPicker uses "Product — Variant"
  const sep = t.indexOf(' — ');
  if (sep > 0) return t.slice(0, sep).trim();
  return t || 'Product';
}

function variantTitleFromCatalog(item: CatalogItem): string {
  if (item.kind === 'variant') {
    const t = String(item.title || '');
    const sep = t.indexOf(' — ');
    if (sep > 0) return t.slice(sep + 3).trim() || 'Default';
  }
  return 'Default';
}

function giftSubtitle(g: GiftProduct): string | null {
  const n = g.selectedVariants?.length || 0;
  const total = g.totalVariants || 0;
  if (n === 0) {
    if (total > 1) return `All ${total} variants`;
    return null;
  }
  if (total > 1) {
    if (n >= total) return `All ${total} variants`;
    return `${n} of ${total} variants`;
  }
  if (n === 1) {
    const vt = g.selectedVariants![0].title;
    if (vt && vt !== 'Default' && vt.toLowerCase() !== 'default title') {
      return vt;
    }
    return null;
  }
  return `${n} variants`;
}

function giftOutOfStock(g: GiftProduct): boolean {
  if (g.outOfStock === true) return true;
  const vs = g.selectedVariants || [];
  if (vs.length > 0) {
    return vs.every((v) => v.available === false);
  }
  return false;
}

function withGiftSubtitle(g: GiftProduct): GiftProduct {
  return {
    ...g,
    subtitle: giftSubtitle(g),
    outOfStock: giftOutOfStock(g),
  };
}

/** Merge raw saved gifts into one row per product (Shopify-style). */
function regroupSavedGifts(
  gifts: Array<{
    id?: string;
    productId?: string;
    variantId?: string | null;
    title?: string;
    imageUrl?: string | null;
    quantity?: number;
    allowedVariantIds?: string[];
    selectedVariants?: Array<{ id: string; title: string; price?: string | null }>;
    totalVariants?: number;
  }>,
): GiftProduct[] {
  const byProduct = new Map<string, GiftProduct>();

  for (const g of gifts) {
    const pid = stripShopifyId(g.productId || g.id);
    if (!pid) continue;

    let productTitle = String(g.title || pid || 'Gift');
    const sep = productTitle.indexOf(' — ');
    if (sep > 0) productTitle = productTitle.slice(0, sep).trim();

    const allowedFromField = Array.isArray(g.allowedVariantIds)
      ? g.allowedVariantIds.map(stripShopifyId).filter(Boolean)
      : [];
    const fromSelected = Array.isArray(g.selectedVariants)
      ? g.selectedVariants
          .map((v) => ({
            id: stripShopifyId(v.id),
            title: v.title || 'Default',
            price: v.price ?? null,
          }))
          .filter((v) => v.id)
      : [];
    const singleVid = g.variantId != null ? stripShopifyId(g.variantId) : '';

    // Build variant list for this row
    let variants: Array<{ id: string; title: string; price?: string | null }> =
      fromSelected.length
        ? fromSelected
        : allowedFromField.map((id) => ({
            id,
            title:
              singleVid === id && sep > 0
                ? String(g.title || '').slice(sep + 3).trim() || 'Default'
                : 'Default',
            price: null,
          }));
    if (!variants.length && singleVid) {
      variants = [
        {
          id: singleVid,
          title:
            sep > 0
              ? String(g.title || '').slice(sep + 3).trim() || 'Default'
              : 'Default',
          price: null,
        },
      ];
    }

    const existing = byProduct.get(pid);
    if (!existing) {
      byProduct.set(
        pid,
        withGiftSubtitle({
          id: pid,
          productId: pid,
          title: productTitle,
          imageUrl: g.imageUrl || null,
          quantity: Math.max(1, Number(g.quantity) || 1),
          selectedVariants: variants,
          totalVariants: g.totalVariants || undefined,
        }),
      );
    } else {
      const merged = new Map(
        (existing.selectedVariants || []).map((v) => [v.id, v]),
      );
      for (const v of variants) {
        if (!merged.has(v.id)) merged.set(v.id, v);
        else if (
          merged.get(v.id)!.title === 'Default' &&
          v.title !== 'Default'
        ) {
          merged.set(v.id, v);
        }
      }
      byProduct.set(
        pid,
        withGiftSubtitle({
          ...existing,
          title:
            existing.title.includes(' — ')
              ? productTitle
              : existing.title || productTitle,
          imageUrl: existing.imageUrl || g.imageUrl || null,
          quantity: Math.max(
            existing.quantity || 1,
            Math.max(1, Number(g.quantity) || 1),
          ),
          selectedVariants: Array.from(merged.values()),
          totalVariants:
            Math.max(existing.totalVariants || 0, g.totalVariants || 0) ||
            existing.totalVariants,
        }),
      );
    }
  }

  return Array.from(byProduct.values());
}

type BrowseKind = 'buy-products' | 'buy-collections' | 'gift-products' | 'tag-products';

export function SpendUnlockGiftWizard({
  editorPreset,
}: {
  /** When set by CampaignEditorPage (edit / resolved route) */
  editorPreset?: 'spend_unlock_gift' | 'quantity_unlock_gift';
} = {}) {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const api = useApi();
  const { currency, formatMoney, moneyPrefix } = useStore();

  const campaignId =
    routeId && routeId !== 'new' && routeId !== 'configure' ? routeId : null;
  const isEdit = Boolean(campaignId);

  const preset =
    editorPreset ||
    searchParams.get('preset') ||
    'spend_unlock_gift';
  const mode: FreeGiftWizardMode =
    preset === 'quantity_unlock_gift' ? 'quantity' : 'spend';
  const isQuantity = mode === 'quantity';

  const [loadingCampaign, setLoadingCampaign] = useState(isEdit);

  const [mainStep, setMainStep] = useState<MainStep>(1);
  const [section, setSection] = useState<SideSection>('buys');

  // Basic
  const [name, setName] = useState(
    isQuantity ? 'Add items, get a free gift' : 'Spend more, unlock a free gift',
  );
  const [schedule, setSchedule] = useState<CampaignScheduleValue>(defaultScheduleValue);

  // Customer buys
  const [appliesTo, setAppliesTo] = useState<AppliesTo>('all');
  const [selectedBuyProducts, setSelectedBuyProducts] = useState<GiftProduct[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<CatalogItem[]>([]);
  const [tagQuery, setTagQuery] = useState('');
  // Main condition: cart value (spend) OR quantity (add X items)
  const [cartValue, setCartValue] = useState('50');
  const [minQuantity, setMinQuantity] = useState('3');

  // Customer gets
  const [giftProducts, setGiftProducts] = useState<GiftProduct[]>([]);
  const [discountCode, setDiscountCode] = useState(() =>
    // FREEBIE_ + 5 random alphanum (unique enforced on save)
    generateFreebieCode(),
  );
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('100');

  // Stacking
  const [priority, setPriority] = useState('10');
  const [stopOnMatch, setStopOnMatch] = useState(true);

  // Advanced — max uses per customer defaults to 1 (once per customer)
  const [maxGiftsPerOrder, setMaxGiftsPerOrder] = useState('');
  const [maxUsesPerCustomer, setMaxUsesPerCustomer] = useState('1');
  const [usageLimitEnabled, setUsageLimitEnabled] = useState(false);
  const [totalUses, setTotalUses] = useState('');
  type Eligibility = 'all' | 'segments' | 'utm' | 'location';
  const [eligibility, setEligibility] = useState<Eligibility>('all');
  const [segmentValue, setSegmentValue] = useState('');
  const [utmValue, setUtmValue] = useState('');
  const [locationCodes, setLocationCodes] = useState<string[]>([]);
  const [locationLabels, setLocationLabels] = useState<Record<string, string>>({});
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  // Display (step 2) — gift selection & widget
  type GiftMethod = 'automatic' | 'customer_chooses';
  // Default automatic: free gifts are added to cart with the main product
  const [giftMethod, setGiftMethod] = useState<GiftMethod>('automatic');
  /**
   * How many free gift units the customer may claim (customer chooses).
   * Tracks the gift product list so selecting 3 gifts → claim 3, not stuck at 1.
   */
  const [freeGiftCount, setFreeGiftCount] = useState('1');
  const prevGiftUnitsRef = useRef(0);
  /** After campaign load, seed unit counters without overwriting saved freeGiftCount */
  const seedGiftUnitsRef = useRef(false);
  const [widgetTitle, setWidgetTitle] = useState(DEFAULT_POPUP_DESIGN.title);
  const [widgetDescription, setWidgetDescription] = useState(DEFAULT_POPUP_DESIGN.description);
  /** Cart Success Notification Bar (auto-add gifts) */
  const [congratsBars, setCongratsBars] = useState(true);
  const [congratsTitle, setCongratsTitle] = useState(DEFAULT_CONGRATS_DESIGN.title);
  const [congratsSubtitle, setCongratsSubtitle] = useState(
    DEFAULT_CONGRATS_DESIGN.subtitle,
  );
  /** PDP promotion widget — off unless merchant explicitly enables */
  const [promoWidgetEnabled, setPromoWidgetEnabled] = useState(false);
  const [promoWidgetTitle, setPromoWidgetTitle] = useState(DEFAULT_PROMO_TITLE);
  const [promoWidgetSubtitle, setPromoWidgetSubtitle] = useState(
    isQuantity ? DEFAULT_PROMO_SUBTITLE_QTY : DEFAULT_PROMO_SUBTITLE_SPEND,
  );

  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseKind, setBrowseKind] = useState<BrowseKind>('gift-products');
  /** Tags frozen at Browse click — never reuse a previous session’s tags */
  const [browseTags, setBrowseTags] = useState<string[]>([]);
  const [browseSession, setBrowseSession] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const embedded = isShopifyEmbedded();

  /** Total free units configured on Customer gets (qty sum). */
  const giftUnits = useMemo(
    () =>
      giftProducts.reduce(
        (s, g) => s + Math.max(1, Number(g.quantity) || 1),
        0,
      ),
    [giftProducts],
  );

  // Keep freeGiftCount aligned with gift list when merchant adds products
  // (3 gifts selected → claim 3). Do not override intentional "pick 1 of N".
  useEffect(() => {
    if (giftUnits <= 0) {
      prevGiftUnitsRef.current = 0;
      return;
    }
    // After load: remember units only (freeGiftCount already set from campaign)
    if (seedGiftUnitsRef.current) {
      seedGiftUnitsRef.current = false;
      prevGiftUnitsRef.current = giftUnits;
      return;
    }
    const prevUnits = prevGiftUnitsRef.current;
    prevGiftUnitsRef.current = giftUnits;

    setFreeGiftCount((prev) => {
      const n = Math.max(1, Number(prev) || 1);
      if (giftUnits < prevUnits) {
        return String(Math.min(n, giftUnits));
      }
      if (giftUnits > prevUnits) {
        // Raised only when pick count was tracking the previous gift total
        // (or first gifts on a new campaign: prev 0, count still default 1)
        if (n === prevUnits || (prevUnits === 0 && n === 1)) {
          return String(giftUnits);
        }
      }
      if (n > giftUnits) return String(giftUnits);
      return prev;
    });

    setMaxGiftsPerOrder((prev) => {
      if (!prev || !String(prev).trim()) {
        return giftUnits > 1 ? String(giftUnits) : prev;
      }
      const n = Math.max(1, Number(prev) || 1);
      if (giftUnits > prevUnits && (n === prevUnits || (prevUnits === 0 && n === 1))) {
        return String(giftUnits);
      }
      if (n < giftUnits) return String(giftUnits);
      return prev;
    });
  }, [giftUnits]);

  const formSnapshot = useMemo(
    () => ({
      name,
      schedule,
      appliesTo,
      selectedBuyProducts,
      selectedCollections,
      tagQuery,
      cartValue,
      minQuantity,
      giftProducts,
      discountCode,
      discountType,
      discountValue,
      priority,
      stopOnMatch,
      maxGiftsPerOrder,
      maxUsesPerCustomer,
      usageLimitEnabled,
      totalUses,
      eligibility,
      segmentValue,
      utmValue,
      locationCodes,
      locationLabels,
      giftMethod,
      freeGiftCount,
      widgetTitle,
      widgetDescription,
      congratsBars,
      congratsTitle,
      congratsSubtitle,
      promoWidgetEnabled,
      promoWidgetTitle,
      promoWidgetSubtitle,
    }),
    [
      name,
      schedule,
      appliesTo,
      selectedBuyProducts,
      selectedCollections,
      tagQuery,
      cartValue,
      minQuantity,
      giftProducts,
      discountCode,
      discountType,
      discountValue,
      priority,
      stopOnMatch,
      maxGiftsPerOrder,
      maxUsesPerCustomer,
      usageLimitEnabled,
      totalUses,
      eligibility,
      segmentValue,
      utmValue,
      locationCodes,
      locationLabels,
      giftMethod,
      freeGiftCount,
      widgetTitle,
      widgetDescription,
      congratsBars,
      congratsTitle,
      congratsSubtitle,
      promoWidgetEnabled,
      promoWidgetTitle,
      promoWidgetSubtitle,
    ],
  );
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(formSnapshot);

  // Establish clean baseline after create defaults render or edit load finishes
  useEffect(() => {
    if (loadingCampaign) return;
    markClean(formSnapshot);
    // only when loading flips false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCampaign]);

  const buyThreshold = isQuantity ? minQuantity || '0' : cartValue || '0';
  const canGoNextFromBuys = Number(buyThreshold) > 0;
  const canGoNextFromGets = giftProducts.length > 0 || discountValue !== '';
  const presetKey = isQuantity ? 'quantity_unlock_gift' : 'spend_unlock_gift';
  const thresholdLabel = isQuantity
    ? `${Number(buyThreshold) || 0} item${Number(buyThreshold) === 1 ? '' : 's'}`
    : formatMoney(Number(buyThreshold) || 0);

  const tagList = useMemo(
    () =>
      tagQuery
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    [tagQuery],
  );

  useEffect(() => {
    if (!campaignId) {
      setLoadingCampaign(false);
      return;
    }
    setLoadingCampaign(true);
    api
      .get<{
        name: string;
        priority?: number;
        startsAt?: string | null;
        endsAt?: string | null;
        maxUsesTotal?: number | null;
        maxUsesPerCustomer?: number | null;
        maxGiftsPerOrder?: number | null;
        config?: Record<string, unknown>;
      }>(`/campaigns/${campaignId}`)
      .then((c) => {
        setName(c.name || '');
        setPriority(String(c.priority ?? 1));
        setSchedule(scheduleFromIso(c.startsAt, c.endsAt));

        const cfg = (c.config || {}) as Record<string, unknown>;
        const fg = (cfg.freeGift || {}) as Record<string, unknown>;
        const conditions = (fg.conditions || {}) as Record<string, unknown>;
        const display = (cfg.display || {}) as Record<string, unknown>;
        const stacking = (cfg.stacking || {}) as Record<string, unknown>;
        const advanced = (cfg.advanced || {}) as Record<string, unknown>;
        const elig = (advanced.customerEligibility || {}) as Record<
          string,
          unknown
        >;
        const discount = (fg.discount || {}) as Record<string, unknown>;

        // Advanced limits: config.advanced is source of truth when present
        const advUsageOff = advanced.usageLimitEnabled === false;
        const advTotal =
          advanced.totalUses != null && Number(advanced.totalUses) > 0
            ? Number(advanced.totalUses)
            : null;
        const topTotal =
          c.maxUsesTotal != null && Number(c.maxUsesTotal) > 0
            ? Number(c.maxUsesTotal)
            : null;
        if (!advUsageOff && (advanced.usageLimitEnabled === true || topTotal || advTotal)) {
          setUsageLimitEnabled(true);
          setTotalUses(String(topTotal ?? advTotal ?? ''));
        } else {
          setUsageLimitEnabled(false);
          setTotalUses('');
        }

        const maxPer =
          advanced.maxUsesPerCustomer != null &&
          Number(advanced.maxUsesPerCustomer) > 0
            ? Number(advanced.maxUsesPerCustomer)
            : c.maxUsesPerCustomer != null && Number(c.maxUsesPerCustomer) > 0
              ? Number(c.maxUsesPerCustomer)
              : 1;
        setMaxUsesPerCustomer(String(Math.max(1, Math.floor(maxPer) || 1)));

        const maxGifts =
          advanced.maxGiftsPerOrder != null &&
          Number(advanced.maxGiftsPerOrder) > 0
            ? Number(advanced.maxGiftsPerOrder)
            : c.maxGiftsPerOrder != null && Number(c.maxGiftsPerOrder) > 0
              ? Number(c.maxGiftsPerOrder)
              : null;
        if (maxGifts != null) setMaxGiftsPerOrder(String(maxGifts));

        if (conditions.minSubtotal != null) setCartValue(String(conditions.minSubtotal));
        if (conditions.minQuantity != null) setMinQuantity(String(conditions.minQuantity));

        if (Array.isArray(conditions.productIds) && conditions.productIds.length) {
          setAppliesTo('products');
          const titles = (conditions.productTitles as string[] | undefined) || [];
          const images = (conditions.productImages as (string | null)[] | undefined) || [];
          // Parallel array — may contain null for product-level rows
          const variantIds =
            (conditions.variantIds as Array<string | null | undefined> | undefined) ||
            [];
          setSelectedBuyProducts(
            (conditions.productIds as string[]).map((pid, i) => {
              const rawVid = variantIds[i];
              const vid =
                rawVid != null && String(rawVid).trim()
                  ? stripShopifyId(String(rawVid))
                  : null;
              const productId = stripShopifyId(String(pid)) || String(pid);
              return {
                id: vid || productId,
                productId,
                variantId: vid,
                title: titles[i] || productId,
                imageUrl: images[i] || null,
              };
            }),
          );
        } else if (Array.isArray(conditions.collectionIds) && conditions.collectionIds.length) {
          setAppliesTo('collections');
          const ids = conditions.collectionIds as string[];
          const titles = (conditions.collectionTitles as string[] | undefined) || [];
          setSelectedCollections(
            ids.map((id, i) => ({
              id,
              title: titles[i] || id,
              kind: 'collection' as const,
            })),
          );
        } else if (Array.isArray(conditions.productTags) && conditions.productTags.length) {
          setAppliesTo('tags');
          setTagQuery((conditions.productTags as string[]).join(', '));
        }

        const gifts = (fg.gifts || []) as Array<{
          id?: string;
          productId?: string;
          variantId?: string | null;
          title?: string;
          imageUrl?: string | null;
          quantity?: number;
          allowedVariantIds?: string[];
          selectedVariants?: Array<{
            id: string;
            title: string;
            price?: string | null;
          }>;
          totalVariants?: number;
        }>;
        if (gifts.length) {
          seedGiftUnitsRef.current = true;
          const regrouped = regroupSavedGifts(gifts);
          setGiftProducts(regrouped);
          const units = regrouped.reduce(
            (s, g) => s + Math.max(1, Number(g.quantity) || 1),
            0,
          );
          const loadedPick = Math.max(1, Number(fg.freeGiftCount) || 1);
          // Stuck default: 3 gifts listed but freeGiftCount still 1 → claim all units
          // (merchant can lower "Number of free gifts customer can select" to pick 1 of N)
          setFreeGiftCount(
            String(loadedPick === 1 && units > 1 ? units : loadedPick),
          );
          prevGiftUnitsRef.current = units;
        } else if (fg.freeGiftCount != null) {
          setFreeGiftCount(String(fg.freeGiftCount));
        }

        if (discount.code) setDiscountCode(String(discount.code));
        if (discount.type) setDiscountType(String(discount.type));
        if (discount.value != null) setDiscountValue(String(discount.value));

        if (fg.autoAdd === true || fg.selectionMode === 'auto') {
          setGiftMethod('automatic');
        } else {
          setGiftMethod('customer_chooses');
        }
        // freeGiftCount set after gifts load so we can fix stuck default "1"

        if (typeof display.title === 'string') setWidgetTitle(display.title);
        if (typeof display.description === 'string') {
          setWidgetDescription(display.description);
        }
        if (typeof display.congratsBars === 'boolean') {
          setCongratsBars(display.congratsBars);
        }
        if (typeof display.congratsTitle === 'string') {
          setCongratsTitle(display.congratsTitle);
        }
        if (typeof display.congratsSubtitle === 'string') {
          setCongratsSubtitle(display.congratsSubtitle);
        }
        setPromoWidgetEnabled(display.promoWidgetEnabled === true);
        if (typeof display.promoWidgetTitle === 'string') {
          setPromoWidgetTitle(display.promoWidgetTitle);
        }
        if (typeof display.promoWidgetSubtitle === 'string') {
          setPromoWidgetSubtitle(display.promoWidgetSubtitle);
        }
        if (typeof stacking.stopOnMatch === 'boolean') {
          setStopOnMatch(stacking.stopOnMatch);
        }

        if (elig.type === 'segments' || elig.type === 'utm' || elig.type === 'location') {
          setEligibility(elig.type);
        } else {
          setEligibility('all');
        }
        setSegmentValue(segmentValueFromEligibility(elig));
        setUtmValue(typeof elig.utm === 'string' ? elig.utm : '');
        if (Array.isArray(elig.locationCodes)) {
          setLocationCodes(elig.locationCodes as string[]);
        } else {
          setLocationCodes([]);
        }
        if (Array.isArray(elig.locations)) {
          const labels: Record<string, string> = {};
          for (const loc of elig.locations as Array<{ code: string; name?: string }>) {
            labels[loc.code] = loc.name || loc.code;
          }
          setLocationLabels(labels);
        } else {
          setLocationLabels({});
        }
      })
      .catch((e: Error) => setError(e.message || 'Failed to load campaign'))
      .finally(() => setLoadingCampaign(false));
  }, [api, campaignId]);

  function openBrowse(kind: BrowseKind) {
    setBrowseKind(kind);
    // Snapshot current tags at click time (not whatever was used last open)
    const tagsNow =
      kind === 'tag-products'
        ? tagQuery
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : [];
    setBrowseTags(tagsNow);
    setBrowseSession((n) => n + 1);
    setBrowseOpen(true);
  }

  function catalogItemToBuyProduct(item: CatalogItem): GiftProduct {
    const productId =
      stripShopifyId(
        item.productId ||
          (item.kind === 'product' ? item.id : undefined) ||
          '',
      ) ||
      (item.kind === 'variant'
        ? stripShopifyId(item.productId || '')
        : stripShopifyId(item.id));
    const variantId = stripShopifyId(
      item.variantId || (item.kind === 'variant' ? item.id : '') || '',
    );
    // Prefer stable product id as row key when product-level; variant id when
    // a specific size/color is chosen (so multi-variant picks stay distinct).
    const rowId =
      item.kind === 'variant' && variantId
        ? variantId
        : productId || variantId || item.id;
    return {
      id: rowId,
      title: item.title,
      imageUrl: item.imageUrl || null,
      quantity: 1,
      productId: productId || undefined,
      variantId: variantId || null,
      outOfStock: item.available === false,
    };
  }

  function buyKeyMatch(a: GiftProduct, b: GiftProduct) {
    if (a.id === b.id) return true;
    // Same specific variant
    if (
      a.variantId &&
      b.variantId &&
      stripShopifyId(a.variantId) === stripShopifyId(b.variantId)
    ) {
      return true;
    }
    // Product-level rows (no variant) match by productId
    if (
      !a.variantId &&
      !b.variantId &&
      a.productId &&
      b.productId &&
      stripShopifyId(a.productId) === stripShopifyId(b.productId)
    ) {
      return true;
    }
    return false;
  }

  /**
   * Gift picker: one row per product. Selecting variants adds them under that
   * product (Shopify “2 of 8 variants”), not separate qty rows.
   * action: undefined = toggle, 'add' | 'remove' = forced (batch multi-variant).
   */
  function applyGiftCatalogToggle(
    prev: GiftProduct[],
    item: CatalogItem,
    action?: 'add' | 'remove',
  ): GiftProduct[] {
    const productId =
      stripShopifyId(
        item.productId ||
          (item.kind === 'product' ? item.id : undefined) ||
          '',
      ) || stripShopifyId(item.id);
    if (!productId) return prev;

    const productTitle = productTitleFromCatalog(item);
    const variantId = stripShopifyId(
      item.variantId || (item.kind === 'variant' ? item.id : '') || '',
    );
    const totalFromItem =
      item.totalVariants ||
      (item.variants && item.variants.length
        ? item.variants.length
        : undefined);

    const idx = prev.findIndex(
      (g) =>
        stripShopifyId(g.productId || g.id) === productId ||
        g.id === productId,
    );
    const existing = idx >= 0 ? prev[idx] : null;

    // Product-level toggle without a concrete variant id
    if (!variantId) {
      if (action === 'add') {
        if (existing) return prev;
        return [
          ...prev,
          withGiftSubtitle({
            id: productId,
            productId,
            title: productTitle,
            imageUrl: item.imageUrl || null,
            quantity: 1,
            selectedVariants: [],
            totalVariants: totalFromItem,
            outOfStock: item.available === false,
          }),
        ];
      }
      if (action === 'remove') {
        return existing ? prev.filter((_, i) => i !== idx) : prev;
      }
      if (existing) return prev.filter((_, i) => i !== idx);
      return [
        ...prev,
        withGiftSubtitle({
          id: productId,
          productId,
          title: productTitle,
          imageUrl: item.imageUrl || null,
          quantity: 1,
          selectedVariants: [],
          totalVariants: totalFromItem,
          outOfStock: item.available === false,
        }),
      ];
    }

    const vTitle = variantTitleFromCatalog(item);
    const vEntry = {
      id: variantId,
      title: vTitle,
      price: item.price ?? null,
      available: item.available !== false,
    };

    if (!existing) {
      if (action === 'remove') return prev;
      return [
        ...prev,
        withGiftSubtitle({
          id: productId,
          productId,
          title: productTitle,
          imageUrl: item.imageUrl || null,
          quantity: 1,
          selectedVariants: [vEntry],
          totalVariants: totalFromItem,
        }),
      ];
    }

    const cur = existing.selectedVariants || [];
    const has = cur.some((v) => stripShopifyId(v.id) === variantId);
    let nextVariants: typeof cur;

    if (action === 'add') {
      if (has) return prev;
      nextVariants = [...cur, vEntry];
    } else if (action === 'remove') {
      if (!has) return prev;
      nextVariants = cur.filter((v) => stripShopifyId(v.id) !== variantId);
      if (nextVariants.length === 0) {
        return prev.filter((_, i) => i !== idx);
      }
    } else if (has) {
      nextVariants = cur.filter((v) => stripShopifyId(v.id) !== variantId);
      if (nextVariants.length === 0) {
        return prev.filter((_, i) => i !== idx);
      }
    } else {
      nextVariants = [...cur, vEntry];
    }

    const next = [...prev];
    next[idx] = withGiftSubtitle({
      ...existing,
      title: productTitle || existing.title,
      imageUrl: existing.imageUrl || item.imageUrl || null,
      selectedVariants: nextVariants,
      totalVariants:
        Math.max(existing.totalVariants || 0, totalFromItem || 0) ||
        existing.totalVariants ||
        totalFromItem,
    });
    return next;
  }

  function toggleGiftFromCatalog(item: CatalogItem) {
    setGiftProducts((prev) => applyGiftCatalogToggle(prev, item));
  }

  function toggleCatalogItem(item: CatalogItem) {
    if (browseKind === 'buy-collections') {
      setSelectedCollections((prev) =>
        prev.some((x) => x.id === item.id)
          ? prev.filter((x) => x.id !== item.id)
          : [...prev, { ...item, kind: 'collection' }],
      );
      return;
    }
    if (browseKind === 'gift-products') {
      toggleGiftFromCatalog(item);
      return;
    }
    if (browseKind === 'buy-products' || browseKind === 'tag-products') {
      const asProduct = catalogItemToBuyProduct(item);
      setSelectedBuyProducts((prev) =>
        prev.some((x) => buyKeyMatch(x, asProduct))
          ? prev.filter((x) => !buyKeyMatch(x, asProduct))
          : [...prev, asProduct],
      );
    }
  }

  function toggleCatalogMany(items: CatalogItem[], action: 'add' | 'remove') {
    if (browseKind === 'gift-products') {
      setGiftProducts((prev) => {
        let next = prev;
        for (const item of items) {
          next = applyGiftCatalogToggle(next, item, action);
        }
        return next;
      });
      return;
    }
    if (browseKind === 'buy-products' || browseKind === 'tag-products') {
      setSelectedBuyProducts((prev) => {
        let next = prev;
        for (const item of items) {
          const asProduct = catalogItemToBuyProduct(item);
          const exists = next.some((x) => buyKeyMatch(x, asProduct));
          if (action === 'add' && !exists) next = [...next, asProduct];
          if (action === 'remove' && exists) {
            next = next.filter((x) => !buyKeyMatch(x, asProduct));
          }
        }
        return next;
      });
    }
  }

  /** Variant ids for gift picker checkboxes (product-grouped selection) */
  const giftBrowseSelectedIds = giftProducts.flatMap((g) => {
    const vids = (g.selectedVariants || []).map((v) => v.id).filter(Boolean);
    if (vids.length) return vids;
    return [g.productId || g.id].filter(Boolean) as string[];
  });

  // Buy products: variant picks → variant id only; product-level → product id.
  // Including productId for every variant made the whole product look selected.
  const buyBrowseSelectedIds = selectedBuyProducts.flatMap((p) => {
    if (p.variantId) return [String(p.variantId)];
    return [p.productId || p.id].filter(
      (x): x is string => Boolean(x && String(x).trim()),
    );
  });

  const browseSelectedIds =
    browseKind === 'gift-products'
      ? giftBrowseSelectedIds
      : browseKind === 'buy-collections'
        ? selectedCollections.map((c) => c.id)
        : buyBrowseSelectedIds;

  function sideNavItem(key: SideSection, label: string, icon: string) {
    const active = section === key && mainStep === 1;
    return (
      <button
        type="button"
        key={key}
        onClick={() => {
          setMainStep(1);
          setSection(key);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          textAlign: 'left',
          padding: '10px 12px',
          borderRadius: 8,
          border: active ? '1.5px solid #2c6ecb' : '1.5px solid transparent',
          background: active ? '#f2f7fe' : 'transparent',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: active ? 600 : 500,
          color: '#202223',
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        {label}
      </button>
    );
  }

  /**
   * Full setup must be complete before save (CSB Save is the only final action).
   * Returns null when valid, otherwise { message, step, section }.
   */
  function validateBeforeSave(): {
    message: string;
    step: MainStep;
    section: SideSection;
  } | null {
    if (!name.trim()) {
      return {
        message: 'Enter a campaign name before saving.',
        step: 1,
        section: 'buys',
      };
    }
    if (Number(buyThreshold) <= 0) {
      return {
        message: isQuantity
          ? 'Set how many items customers must buy (greater than 0).'
          : 'Set the cart spend amount required to unlock the gift (greater than 0).',
        step: 1,
        section: 'buys',
      };
    }
    if (appliesTo === 'products' && selectedBuyProducts.length === 0) {
      return {
        message: 'Select at least one product that counts toward this offer.',
        step: 1,
        section: 'buys',
      };
    }
    if (appliesTo === 'collections' && selectedCollections.length === 0) {
      return {
        message: 'Select at least one collection that counts toward this offer.',
        step: 1,
        section: 'buys',
      };
    }
    if (appliesTo === 'tags' && tagList.length === 0) {
      return {
        message: 'Add at least one product tag that counts toward this offer.',
        step: 1,
        section: 'buys',
      };
    }
    if (giftProducts.length === 0) {
      return {
        message: 'Add at least one free gift product before saving.',
        step: 1,
        section: 'gets',
      };
    }
    if (!discountCode.trim()) {
      return {
        message: 'Enter a discount code for the free gift.',
        step: 1,
        section: 'gets',
      };
    }
    if (Number(discountValue) < 0 || discountValue === '') {
      return {
        message: 'Set a valid discount value for the free gift.',
        step: 1,
        section: 'gets',
      };
    }
    if (giftMethod === 'customer_chooses' && Number(freeGiftCount) < 1) {
      return {
        message: 'Set how many free gifts the customer can choose (at least 1).',
        step: 2,
        section: 'gets',
      };
    }
    if (eligibility === 'segments' && !segmentIdsFromValue(segmentValue).length) {
      return {
        message: 'Select at least one customer segment, or set eligibility to all customers.',
        step: 1,
        section: 'advanced',
      };
    }
    if (eligibility === 'utm' && !utmValue.trim()) {
      return {
        message: 'Enter a UTM value, or set eligibility to all customers.',
        step: 1,
        section: 'advanced',
      };
    }
    if (eligibility === 'location' && locationCodes.length === 0) {
      return {
        message: 'Select at least one location, or set eligibility to all customers.',
        step: 1,
        section: 'advanced',
      };
    }
    if (usageLimitEnabled && (!totalUses || Number(totalUses) < 1)) {
      return {
        message: 'Enter a total usage limit greater than 0, or turn off the usage limit.',
        step: 1,
        section: 'advanced',
      };
    }
    return null;
  }

  async function save(activate: boolean) {
    const invalid = validateBeforeSave();
    if (invalid) {
      setMainStep(invalid.step);
      setSection(invalid.section);
      setError(invalid.message);
      setMessage(null);
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      // Ensure discount code is unique across store campaigns
      const uniqueCode = await resolveUniqueDiscountCode(
        api,
        'FREEBIE_',
        discountCode,
        campaignId || undefined,
      );
      if (uniqueCode !== discountCode.trim().toUpperCase()) {
        setDiscountCode(uniqueCode);
      }

      const conditions: Record<string, unknown> = isQuantity
        ? { minQuantity: Number(minQuantity || 0) }
        : { minSubtotal: Number(cartValue || 0) };

      if (appliesTo === 'products') {
        // Keep parallel arrays aligned (never .filter(Boolean) on variantIds alone)
        conditions.productIds = selectedBuyProducts.map(
          (p) => stripShopifyId(p.productId || p.id) || p.productId || p.id,
        );
        conditions.variantIds = selectedBuyProducts.map(
          (p) => (p.variantId ? stripShopifyId(p.variantId) : null),
        );
        conditions.productTitles = selectedBuyProducts.map((p) => p.title);
        conditions.productImages = selectedBuyProducts.map(
          (p) => p.imageUrl || null,
        );
      }
      if (appliesTo === 'collections') {
        conditions.collectionIds = selectedCollections.map((c) => c.id);
        conditions.collectionTitles = selectedCollections.map((c) => c.title);
        conditions.collectionHandles = selectedCollections.map(
          (c) => c.handle || '',
        );
      }
      if (appliesTo === 'tags' && tagList.length) {
        // Tags are enforced at evaluate (expand tag → products). Optional
        // browse picks are also saved as product scope when present.
        conditions.productTags = tagList;
        if (selectedBuyProducts.length) {
          conditions.productIds = selectedBuyProducts.map(
            (p) => stripShopifyId(p.productId || p.id) || p.productId || p.id,
          );
          conditions.variantIds = selectedBuyProducts.map(
            (p) => (p.variantId ? stripShopifyId(p.variantId) : null),
          );
          conditions.productTitles = selectedBuyProducts.map((p) => p.title);
          conditions.productImages = selectedBuyProducts.map(
            (p) => p.imageUrl || null,
          );
        }
      }

      const units = Math.max(
        1,
        giftProducts.reduce(
          (s, g) => s + Math.max(1, Number(g.quantity) || 1),
          0,
        ),
      );
      // Free units the shopper may claim.
      // - Automatic OR single gift product → always qty sum (qty 4 → 4 free units)
      // - Multi-product customer chooses → pick field (how many products to select)
      const claimCount =
        giftMethod === 'automatic' || giftProducts.length <= 1
          ? units
          : Math.max(1, Number(freeGiftCount) || units);
      const maxGiftsSaved = Math.max(
        claimCount,
        units,
        maxGiftsPerOrder.trim()
          ? Math.max(1, Math.floor(Number(maxGiftsPerOrder)) || 1)
          : 0,
      );

      const config = {
        freeGift: {
          // automatic → all free gifts added to cart; customer_chooses → pick up to freeGiftCount
          selectionMode: giftMethod === 'automatic' ? 'auto' : 'manual',
          limitMode:
            giftMethod === 'automatic' || claimCount > 1
              ? 'multiple'
              : 'one',
          autoAdd: giftMethod === 'automatic',
          freeGiftCount: claimCount,
          allowReplacement: giftMethod === 'customer_chooses',
          conditionType: isQuantity ? 'quantity' : 'cart_value',
          gifts: giftProducts.map((g, i) => {
            const pid = stripShopifyId(g.productId || g.id) || g.productId || g.id;
            const vids = (g.selectedVariants || [])
              .map((v) => stripShopifyId(v.id))
              .filter(Boolean);
            // Single selected variant → pin it; multi → allowedVariantIds for popup
            const singleVid = vids.length === 1 ? vids[0] : null;
            return {
              id: pid,
              productId: pid,
              variantId: singleVid,
              allowedVariantIds: vids.length ? vids : undefined,
              selectedVariants: g.selectedVariants || [],
              totalVariants: g.totalVariants,
              title: g.title,
              imageUrl: g.imageUrl || null,
              quantity: Math.max(1, Number(g.quantity) || 1),
              priority: i + 1,
              inventoryTracked: false,
            };
          }),
          conditions,
          discount: {
            code: uniqueCode,
            type: discountType,
            value: Number(discountValue),
          },
        },
        display: {
          giftMethod,
          chooseUi: giftMethod === 'customer_chooses' ? 'popup' : null,
          title: widgetTitle,
          description: widgetDescription,
          /** Full visual design lives on the Widgets → Pop-up editor */
          widgetType: 'POPUP',
          congratsBars,
          congratsTitle: congratsTitle.trim() || DEFAULT_CONGRATS_DESIGN.title,
          congratsSubtitle:
            congratsSubtitle.trim() || DEFAULT_CONGRATS_DESIGN.subtitle,
          promoWidgetEnabled: promoWidgetEnabled === true,
          promoWidgetTitle: promoWidgetTitle.trim() || DEFAULT_PROMO_TITLE,
          promoWidgetSubtitle:
            promoWidgetSubtitle.trim() ||
            (isQuantity
              ? DEFAULT_PROMO_SUBTITLE_QTY
              : DEFAULT_PROMO_SUBTITLE_SPEND),
        },
        stacking: {
          priority: Math.max(0, Math.floor(Number(priority) || 0)),
          stopOnMatch,
          /** Always on — free gifts are removed when cart no longer qualifies */
          worksInReverse: true,
        },
        advanced: {
          usageLimitEnabled,
          totalUses:
            usageLimitEnabled && totalUses.trim()
              ? Math.max(1, Math.floor(Number(totalUses)) || 1)
              : null,
          maxUsesPerCustomer: Math.max(
            1,
            Math.floor(Number(maxUsesPerCustomer)) || 1,
          ),
          maxGiftsPerOrder: maxGiftsSaved,
          customerEligibility: {
            type: eligibility,
            segments:
              eligibility === 'segments'
                ? segmentNamesFromValue(segmentValue)
                : [],
            segmentIds:
              eligibility === 'segments'
                ? segmentIdsFromValue(segmentValue)
                : [],
            utm: eligibility === 'utm' ? utmValue.trim() : null,
            locationCodes: eligibility === 'location' ? locationCodes : [],
            locations:
              eligibility === 'location'
                ? locationCodes.map((code) => ({
                    code,
                    name: locationLabels[code] || code,
                  }))
                : [],
          },
        },
      };

      const defaultName = isQuantity
        ? 'Add items, get a free gift'
        : 'Spend more, unlock a free gift';
      const description = isQuantity
        ? `Unlock free gift when cart has ${Number(minQuantity) || 0}+ items`
        : `Unlock free gift at ${formatMoney(Number(cartValue) || 0)}+ cart value`;

      const { startsAt, endsAt } = scheduleToIso(schedule);
      // Always send null (not omit) so edit clears previous schedule / limits
      const payload = {
        name: name || defaultName,
        description,
        type: 'FREE_GIFT' as const,
        priority: Math.max(0, Math.floor(Number(priority) || 0)),
        status: 'ACTIVE',
        startsAt: startsAt || null,
        endsAt: endsAt || null,
        config,
        gifts: config.freeGift.gifts,
        maxGiftsPerOrder: maxGiftsSaved,
        maxUsesTotal:
          usageLimitEnabled && totalUses.trim()
            ? Math.max(1, Math.floor(Number(totalUses)) || 1)
            : null,
        maxUsesPerCustomer: Math.max(
          1,
          Math.floor(Number(maxUsesPerCustomer)) || 1,
        ),
        tags: [presetKey],
      };

      if (campaignId) {
        await api.patch(`/campaigns/${campaignId}`, payload);
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
      } else {
        const created = await api.post<{ id: string }>('/campaigns', payload);
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
        navigate(`/campaigns/${created.id}?preset=${presetKey}`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const b = getBaseline();
    if (!b) return;
    setName(b.name);
    setSchedule(b.schedule);
    setAppliesTo(b.appliesTo);
    setSelectedBuyProducts(b.selectedBuyProducts);
    setSelectedCollections(b.selectedCollections);
    setTagQuery(b.tagQuery);
    setCartValue(b.cartValue);
    setMinQuantity(b.minQuantity);
    setGiftProducts(b.giftProducts);
    setDiscountCode(b.discountCode);
    setDiscountType(b.discountType);
    setDiscountValue(b.discountValue);
    setPriority(b.priority);
    setStopOnMatch(b.stopOnMatch);
    setMaxGiftsPerOrder(b.maxGiftsPerOrder);
    setMaxUsesPerCustomer(b.maxUsesPerCustomer);
    setUsageLimitEnabled(b.usageLimitEnabled);
    setTotalUses(b.totalUses);
    setEligibility(b.eligibility);
    setSegmentValue(b.segmentValue);
    setUtmValue(b.utmValue);
    setLocationCodes(b.locationCodes);
    setLocationLabels(b.locationLabels);
    setGiftMethod(b.giftMethod);
    setFreeGiftCount(b.freeGiftCount);
    setWidgetTitle(b.widgetTitle);
    setWidgetDescription(b.widgetDescription);
    setCongratsBars(b.congratsBars !== false);
    setCongratsTitle(b.congratsTitle || DEFAULT_CONGRATS_DESIGN.title);
    setCongratsSubtitle(b.congratsSubtitle || DEFAULT_CONGRATS_DESIGN.subtitle);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(b);
  }

  function renderBuys() {
    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Customer buys")}
        </Text>
        <Text as="p" tone="subdued">
          {isQuantity
            ? t(
                'Which products count toward the item quantity required for a free gift?',
              )
            : t('Which products count toward unlocking the free gift?')}
        </Text>

        <BlockStack gap="200">
          {(
            [
              ['all', t('All products')],
              ['collections', t('Collections')],
              ['tags', t('Product tags')],
              ['products', t('Specific products')],
            ] as const
          ).map(([value, label]) => (
            <RadioButton
              key={value}
              label={label}
              checked={appliesTo === value}
              id={`applies-${value}`}
              name="appliesTo"
              onChange={() => setAppliesTo(value)}
            />
          ))}
        </BlockStack>

        {appliesTo === 'products' && (
          <BlockStack gap="200">
            <InlineStack gap="300" blockAlign="center">
              <Text as="span" tone="subdued">
                {selectedBuyProducts.length} product
                {selectedBuyProducts.length === 1 ? '' : 's'} selected
              </Text>
              <Button onClick={() => openBrowse('buy-products')}>
                {t("Browse products")}
              </Button>
            </InlineStack>
            {selectedBuyProducts.map((p) => (
              <SelectedProductRow
                key={p.id}
                product={{
                  ...p,
                  outOfStock: giftOutOfStock(p),
                }}
                showQuantity={false}
                onRemove={() =>
                  setSelectedBuyProducts((prev) =>
                    prev.filter((x) => x.id !== p.id),
                  )
                }
              />
            ))}
          </BlockStack>
        )}
        {appliesTo === 'collections' && (
          <BlockStack gap="200">
            <InlineStack gap="300" blockAlign="center">
              <Text as="span" tone="subdued">
                {selectedCollections.length} collection
                {selectedCollections.length === 1 ? '' : 's'} selected
              </Text>
              <Button onClick={() => openBrowse('buy-collections')}>
                {t("Browse collections")}
              </Button>
            </InlineStack>
            {selectedCollections.map((c) => (
              <InlineStack key={c.id} align="space-between">
                <Text as="span">{c.title}</Text>
                <Button
                  variant="plain"
                  tone="critical"
                  onClick={() =>
                    setSelectedCollections((prev) =>
                      prev.filter((x) => x.id !== c.id),
                    )
                  }
                >
                  {t("Remove")}
                </Button>
              </InlineStack>
            ))}
          </BlockStack>
        )}
        {appliesTo === 'tags' && (
          <BlockStack gap="200">
            <TextField
              label={t("Product tags")}
              value={tagQuery}
              onChange={setTagQuery}
              autoComplete="off"
              helpText={t("Enter tags here (comma-separated). Then browse to pick products that have these tags.")}
              placeholder={t("sale, summer")}
            />
            <InlineStack gap="200" blockAlign="center">
              <Button
                onClick={() => openBrowse('tag-products')}
                disabled={tagList.length === 0}
              >
                {t("Browse products with these tags")}
              </Button>
              {selectedBuyProducts.length > 0 && (
                <Text as="span" tone="subdued" variant="bodySm">
                  {selectedBuyProducts.length} product
                  {selectedBuyProducts.length === 1 ? '' : 's'} linked
                </Text>
              )}
            </InlineStack>
            {selectedBuyProducts.map((p) => (
              <SelectedProductRow
                key={p.id}
                product={{
                  ...p,
                  outOfStock: giftOutOfStock(p),
                }}
                showQuantity={false}
                onRemove={() =>
                  setSelectedBuyProducts((prev) =>
                    prev.filter((x) => x.id !== p.id),
                  )
                }
              />
            ))}
          </BlockStack>
        )}

        <Divider />

        <Text as="h3" variant="headingSm">
          {t("Offer main condition")}
        </Text>
        <Box
          padding="300"
          background="bg-surface-secondary"
          borderRadius="200"
          borderWidth="025"
          borderColor="border"
        >
          <BlockStack gap="200">
            <Text as="span" fontWeight="semibold">
              {isQuantity ? 'Quantity condition' : 'Cart value condition'}
            </Text>
            {isQuantity ? (
              <TextField
                label={t("Buy at least")}
                type="number"
                value={minQuantity}
                onChange={setMinQuantity}
                suffix="items"
                autoComplete="off"
                helpText={t("Add at least this many qualifying items to unlock free gifts.")}
                requiredIndicator
              />
            ) : (
              <TextField
                label={t("Buy at least")}
                type="number"
                value={cartValue}
                onChange={setCartValue}
                prefix={moneyPrefix}
                autoComplete="off"
                helpText={`Spend at least this amount (${currency}) to unlock free gifts.`}
                requiredIndicator
              />
            )}
          </BlockStack>
        </Box>
      </BlockStack>
    );
  }

  function renderGets() {
    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Customer gets")}
        </Text>
        <Text as="p" tone="subdued">
          {t("Choose free gift products and how the discount is applied.")}
        </Text>

        <InlineStack gap="200" blockAlign="end" wrap>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Button onClick={() => openBrowse('gift-products')}>
              {t("Browse Shopify products")}
            </Button>
          </div>
        </InlineStack>

        {giftProducts.length > 0 ? (
          <BlockStack gap="200">
            {giftProducts.map((g) => (
              <SelectedProductRow
                key={g.productId || g.id}
                product={{
                  ...g,
                  subtitle: g.subtitle ?? giftSubtitle(g),
                  outOfStock: giftOutOfStock(g),
                }}
                showQuantity
                onQuantityChange={(quantity) =>
                  setGiftProducts((prev) =>
                    prev.map((x) =>
                      (x.productId || x.id) === (g.productId || g.id)
                        ? { ...x, quantity }
                        : x,
                    ),
                  )
                }
                onRemove={() =>
                  setGiftProducts((prev) =>
                    prev.filter(
                      (x) => (x.productId || x.id) !== (g.productId || g.id),
                    ),
                  )
                }
                onEdit={() => openBrowse('gift-products')}
              />
            ))}
          </BlockStack>
        ) : (
          <Text as="p" tone="subdued" variant="bodySm">
            {t("No gifts selected yet — browse the catalog to add free gifts.\n            Select specific variants under a product; quantity is per product\n            (not per variant).")}
          </Text>
        )}

        <Text as="p" tone="subdued" variant="bodySm">
          {t("How gifts are delivered is configured in Step 2: Display setting (automatic add-all vs\n          customer chooses).")}
        </Text>

        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("Discount config")}
            </Text>
            <TextField
              label={t("Discount code")}
              value={discountCode}
              onChange={(v) => setDiscountCode(v.toUpperCase().replace(/\s+/g, ''))}
              autoComplete="off"
              helpText={t("Auto-generated FREEBIE_XXXXX. You can edit it; codes must be unique across campaigns.")}
              placeholder="FREEBIE_A3K9M"
            />
            <InlineStack gap="300" wrap>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Select
                  label={t("Type")}
                  options={[
                    { label: t("Percentage"), value: 'percentage' },
                    { label: t("Fixed amount"), value: 'fixed' },
                    { label: t("Free (100%)"), value: 'free' },
                  ]}
                  value={discountType}
                  onChange={(v) => {
                    setDiscountType(v);
                    if (v === 'free') setDiscountValue('100');
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <TextField
                  label={t("Value")}
                  type="number"
                  value={discountValue}
                  onChange={setDiscountValue}
                  prefix={discountType === 'fixed' ? moneyPrefix : '%'}
                  autoComplete="off"
                  disabled={discountType === 'free'}
                />
              </div>
            </InlineStack>
            <Text as="p" tone="subdued" variant="bodySm">
              {t("Free gifts typically use 100% off the gift line. Shopify Functions can enforce this\n              later.")}
            </Text>
          </BlockStack>
        </Box>
      </BlockStack>
    );
  }

  function renderStacking() {
    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Priority & stacking")}
        </Text>
        <TextField
          label={t("Priority")}
          type="number"
          value={priority}
          onChange={setPriority}
          helpText={t("Higher number wins. When two campaigns unlock, only the higher-priority free gift and coupon are applied.")}
          autoComplete="off"
        />
        <Checkbox
          label={t("Don't combine with other Freebi offers")}
          checked={stopOnMatch}
          onChange={setStopOnMatch}
          helpText={t("When this campaign unlocks, lower-priority Freebi campaigns won't add gifts or apply their coupon. Turn off only if you intentionally want Freebi offers to stack.")}
        />
      </BlockStack>
    );
  }

  function renderAdvanced() {
    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Advanced settings")}
        </Text>

        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("Usage limit")}
            </Text>
            <Checkbox
              label={t("Limit total uses")}
              checked={usageLimitEnabled}
              onChange={setUsageLimitEnabled}
              helpText={t("When enabled, this campaign stops after the total use count is reached.")}
            />
            <TextField
              label={t("Total uses")}
              type="number"
              value={totalUses}
              onChange={setTotalUses}
              autoComplete="off"
              disabled={!usageLimitEnabled}
              placeholder={t("e.g. 1000")}
              helpText={
                usageLimitEnabled
                  ? 'Maximum times this offer can be redeemed across all customers.'
                  : 'Turn on the toggle above to set a total use limit.'
              }
            />
            <TextField
              label={t("Max uses per customer")}
              type="number"
              min={1}
              value={maxUsesPerCustomer}
              onChange={(v) => {
                if (v === '') {
                  setMaxUsesPerCustomer('');
                  return;
                }
                const n = Math.floor(Number(v));
                setMaxUsesPerCustomer(
                  Number.isFinite(n) && n >= 1 ? String(n) : '1',
                );
              }}
              autoComplete="off"
              helpText={t("How many times each customer can use this offer (default 1 = once).")}
            />
            <TextField
              label={t("Max gifts per order")}
              type="number"
              value={maxGiftsPerOrder}
              onChange={setMaxGiftsPerOrder}
              autoComplete="off"
            />
          </BlockStack>
        </Box>

        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("Customer eligibility")}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {t("Who can receive this free gift?")}
            </Text>
            <BlockStack gap="200">
              {(
                [
                  ['all', t('All customers')],
                  ['segments', t('Specific customer segments')],
                  ['utm', t('UTM link')],
                  ['location', t('Customer location')],
                ] as const
              ).map(([value, label]) => (
                <RadioButton
                  key={value}
                  label={label}
                  checked={eligibility === value}
                  id={`elig-${value}`}
                  name="eligibility"
                  onChange={() => setEligibility(value)}
                />
              ))}
            </BlockStack>

            {eligibility === 'segments' && (
              <CustomerSegmentField
                value={segmentValue}
                onChange={setSegmentValue}
                helpText={t("Type a segment name to search your Shopify customer segments.")}
              />
            )}
            {eligibility === 'utm' && (
              <TextField
                label={t("UTM parameter")}
                value={utmValue}
                onChange={setUtmValue}
                autoComplete="off"
                placeholder={t("utm_campaign=freegift")}
                helpText={t("Only customers arriving with this UTM value qualify.")}
              />
            )}
            {eligibility === 'location' && (
              <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <Text as="span" tone="subdued">
                    {locationCodes.length === 0
                      ? 'No locations selected'
                      : `${locationCodes.length} location${locationCodes.length === 1 ? '' : 's'} selected`}
                  </Text>
                  <Button onClick={() => setLocationPickerOpen(true)}>
                    {locationCodes.length ? 'Edit locations' : 'Browse locations'}
                  </Button>
                </InlineStack>
                {locationCodes.length > 0 && (
                  <InlineStack gap="100" wrap>
                    {locationCodes.map((code) => (
                      <Tag
                        key={code}
                        onRemove={() => {
                          setLocationCodes((prev) =>
                            prev.filter((c) => c !== code),
                          );
                          setLocationLabels((prev) => {
                            const next = { ...prev };
                            delete next[code];
                            return next;
                          });
                        }}
                      >
                        {locationLabels[code] || code}
                      </Tag>
                    ))}
                  </InlineStack>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </Box>

        <CampaignScheduleFields value={schedule} onChange={setSchedule} />
      </BlockStack>
    );
  }

  function methodCard(
    active: boolean,
    onClick: () => void,
    icon: string,
    title: string,
    subtitle: string,
    disabled?: boolean,
    children?: ReactNode,
  ) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          padding: 16,
          borderRadius: 12,
          border: active ? '1.5px solid #008060' : '1px solid #e1e3e5',
          background: disabled ? '#fafbfb' : active ? '#f6fef9' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <InlineStack gap="300" blockAlign="start" wrap={false}>
          <div
            style={{
              width: 18,
              height: 18,
              marginTop: 2,
              borderRadius: '50%',
              border: active ? '5px solid #008060' : '2px solid #8c9196',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1 }}>
            <Text as="p" fontWeight="semibold">
              {icon} {title}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {subtitle}
            </Text>
            {children}
          </div>
        </InlineStack>
      </button>
    );
  }

  function renderDisplay() {
    const pickCount = Math.max(1, Number(freeGiftCount) || 1);
    const previewGifts =
      giftProducts.length > 0
        ? giftProducts.map((g) => ({ id: g.id, title: g.title, meta: 'Free gift' }))
        : undefined;

    const campaignReturn = `/campaigns/new/configure?type=FREE_GIFT&preset=${presetKey}&currency=${currency}`;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 320px',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                {t("Gift selection method")}
              </Text>
              <Text as="p" tone="subdued">
                {t("Choose how customers will receive their gifts")}
              </Text>

              {methodCard(
                giftMethod === 'automatic',
                () => setGiftMethod('automatic'),
                '⚡',
                t('Automatically'),
                t('All free gifts are added to cart when conditions are met'),
              )}

              {methodCard(
                giftMethod === 'customer_chooses',
                () => setGiftMethod('customer_chooses'),
                '🎁',
                t('Customer chooses'),
                t('Customer selects from available gifts in a pop-up'),
                false,
                giftMethod === 'customer_chooses' ? (
                  <div style={{ marginTop: 12 }}>
                    <TextField
                      label={t("Number of free gifts customer can select")}
                      type="number"
                      min={1}
                      value={freeGiftCount}
                      onChange={setFreeGiftCount}
                      autoComplete="off"
                      helpText={
                        giftUnits > 0
                          ? `You listed ${giftUnits} free gift unit${giftUnits === 1 ? '' : 's'}. Customer can claim up to ${pickCount}. Set this to ${giftUnits} to give all selected gifts.`
                          : `Customer can pick up to ${pickCount} gift${pickCount === 1 ? '' : 's'}.`
                      }
                    />
                  </div>
                ) : null,
              )}
            </BlockStack>
          </Card>

          {giftMethod === 'customer_chooses' && (
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                      {t("Pop-up message")}
                    </Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t("Title and description for this campaign’s pop-up. Colors, Auto-fit theme, and\n                      layout are shared under Widgets → Pop-up.")}
                    </Text>
                  </BlockStack>
                  <Badge>{t("Pop-up")}</Badge>
                </InlineStack>
                <TextField
                  label={t("Title")}
                  value={widgetTitle}
                  onChange={setWidgetTitle}
                  autoComplete="off"
                />
                <TextField
                  label={t("Description")}
                  value={widgetDescription}
                  onChange={setWidgetDescription}
                  multiline={3}
                  autoComplete="off"
                />
                <Button
                  onClick={() =>
                    navigate(`/widgets/popup?return=${encodeURIComponent(campaignReturn)}`)
                  }
                >
                  {t("Edit pop-up")}
                </Button>
              </BlockStack>
            </Card>
          )}

          {giftMethod === 'automatic' && (
            <>
              <Banner tone="info">
                <p>
                  {t("When the cart has")}{' '}
                  {isQuantity
                    ? `at least ${thresholdLabel}`
                    : `at least ${thresholdLabel} in value`}
                  , <strong>all</strong> {t("selected free gifts (")}
                  {giftProducts.length || 0}{t(") are added to the cart automatically.")}
                </p>
              </Banner>
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center" wrap>
                    <BlockStack gap="100">
                      <Text as="h2" variant="headingMd">
                        {t("Cart Success Notification Bar")}
                      </Text>
                      <Text as="p" tone="subdued" variant="bodySm">
                        {t("Toast when free gifts are added. Global style under\n                        Widgets → Cart Success Notification Bar.")}
                      </Text>
                    </BlockStack>
                    <Badge>{t("Notification")}</Badge>
                  </InlineStack>
                  <Checkbox
                    label={t("Show when gifts are added to cart")}
                    checked={congratsBars}
                    onChange={setCongratsBars}
                  />
                  {congratsBars && (
                    <>
                      <TextField
                        label={t("Title")}
                        value={congratsTitle}
                        onChange={setCongratsTitle}
                        autoComplete="off"
                        placeholder={DEFAULT_CONGRATS_DESIGN.title}
                      />
                      <TextField
                        label={t("Subtitle")}
                        value={congratsSubtitle}
                        onChange={setCongratsSubtitle}
                        autoComplete="off"
                        placeholder={DEFAULT_CONGRATS_DESIGN.subtitle}
                      />
                      <Button
                        onClick={() =>
                          navigate(
                            `/widgets/congrats?return=${encodeURIComponent(campaignReturn)}`,
                          )
                        }
                      >
                        {t("Edit Cart Success Notification Bar style")}
                      </Button>
                    </>
                  )}
                </BlockStack>
              </Card>
            </>
          )}

          <CampaignPromoWidgetCard
            enabled={promoWidgetEnabled}
            onEnabledChange={setPromoWidgetEnabled}
            title={promoWidgetTitle}
            onTitleChange={setPromoWidgetTitle}
            subtitle={promoWidgetSubtitle}
            onSubtitleChange={setPromoWidgetSubtitle}
            triggerValue={
              isQuantity
                ? String(Math.max(1, Number(minQuantity) || 1))
                : formatMoney(Number(cartValue) || 0)
            }
            gifts={giftProducts.map((g) => {
              const raw =
                g.selectedVariants?.find((v) => v.price)?.price ?? null;
              const orig = parseMoneyAmount(raw);
              const offered =
                orig == null
                  ? null
                  : offeredAmount(orig, discountType, discountValue);
              return {
                id: String(g.productId || g.id),
                title: g.title,
                imageUrl: g.imageUrl,
                priceLabel:
                  orig != null ? formatMoney(orig) : raw ? String(raw) : null,
                offeredLabel:
                  offered == null
                    ? null
                    : offered <= 0.005
                      ? 'Free'
                      : formatMoney(offered),
              };
            })}
            campaignReturn={campaignReturn}
          />
        </BlockStack>

        <BlockStack gap="400">
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t("Preview")}
                </Text>
                <Text as="span" tone="subdued" variant="bodySm">
                  {t("Mobile")}
                </Text>
              </InlineStack>
              {giftMethod === 'customer_chooses' ? (
                <PopupPreview
                  design={{
                    ...DEFAULT_POPUP_DESIGN,
                    title: widgetTitle,
                    description: widgetDescription,
                  }}
                  mode="mobile"
                  selectedCount={pickCount}
                  gifts={previewGifts}
                />
              ) : congratsBars ? (
                <CongratsPreview
                  design={{
                    ...DEFAULT_CONGRATS_DESIGN,
                    title: congratsTitle,
                    subtitle: congratsSubtitle,
                  }}
                  mode="mobile"
                  progressPercent={70}
                />
              ) : (
                <div
                  style={{
                    background: '#f6f6f7',
                    borderRadius: 12,
                    padding: 16,
                    border: '1px solid #e1e3e5',
                  }}
                >
                  <Text as="p" fontWeight="semibold">
                    {t("Free gifts unlocked")}
                  </Text>
                  <Text as="p" tone="subdued" variant="bodySm">
                    {t("All gift items were added to the cart automatically.")}
                  </Text>
                </div>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingSm">
                  {t("Summary")}
                </Text>
                <Badge tone="success">{t("Active")}</Badge>
              </InlineStack>
              <Text as="p" fontWeight="semibold">
                {isQuantity
                  ? `Buy at least ${thresholdLabel} to get `
                  : `Spend at least ${thresholdLabel} to get `}
                {giftMethod === 'automatic'
                  ? `${giftProducts.length || 0} item(s) for free`
                  : `${pickCount} free gift${pickCount === 1 ? '' : 's'}`}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Method:")}{' '}
                {giftMethod === 'automatic'
                  ? t('Automatically (all gifts)')
                  : t('Customer chooses · Pop-up')}
              </Text>
            </BlockStack>
          </Card>
        </BlockStack>
      </div>
    );
  }

  function renderMain() {
    if (section === 'gets') return renderGets();
    if (section === 'stacking') return renderStacking();
    if (section === 'advanced') return renderAdvanced();
    return renderBuys();
  }

  const typeLabel = isQuantity ? 'Add X items' : 'Spend X';
  const pageLabel = campaignPageLabel(isEdit, typeLabel);

  return (
    <Page
      title={pageLabel}
      subtitle={name.trim() || undefined}
      backAction={{ content: t("Campaigns"), onAction: () => navigate('/campaigns') }}
      primaryAction={
        !embedded
          ? {
              content: t("Save"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(true),
            }
          : undefined
      }
    >
      <ContextualSaveBar
        id="freebi-spend-gift-save"
        open={embedded && dirty && !loadingCampaign}
        saving={saving}
        onSave={() => save(true)}
        onDiscard={discard}
      />
      <SavingOverlay
        open={saving}
        label={t("Saving campaign…")}
        detail="Syncing free gift settings and Shopify discounts"
      />
      <BlockStack gap="400">
        <CampaignNameEditor
          name={name}
          onChange={setName}
          placeholder={
            isQuantity
              ? 'Add items, get a free gift'
              : 'Spend more, unlock a free gift'
          }
          defaultEditing={!isEdit && !name.trim()}
        />
        {loadingCampaign && (
          <Banner tone="info">{t("Loading campaign…")}</Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}

        {/* Step switcher */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e1e3e5',
            padding: 6,
          }}
        >
          {([1, 2] as const).map((step) => (
            <button
              key={step}
              type="button"
              onClick={() => setMainStep(step)}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '12px 16px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                background: mainStep === step ? '#e4e5e7' : 'transparent',
                color: '#202223',
              }}
            >
              {t("Step")} {step}:{' '}
              {step === 1 ? t('Campaign setting') : t('Display setting')}
            </button>
          ))}
        </div>

        {mainStep === 2 ? (
          <BlockStack gap="400">
            {renderDisplay()}
            <InlineStack align="start">
              <Button
                onClick={() => {
                  setMainStep(1);
                  setSection('advanced');
                }}
              >
                {t("Back to campaign settings")}
              </Button>
            </InlineStack>
          </BlockStack>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 280px',
              gap: 16,
              alignItems: 'start',
            }}
          >
            <Card padding="0">
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
                <div
                  style={{
                    borderRight: '1px solid #e1e3e5',
                    padding: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  {sideNavItem('buys', t('Customer buys'), '🛒')}
                  {sideNavItem('gets', t('Customer gets'), '🎁')}
                  {sideNavItem('stacking', t('Priority & stacking'), '🔗')}
                  {sideNavItem('advanced', t('Advanced settings'), '⚙️')}
                </div>
                <div style={{ padding: 20 }}>
                  {renderMain()}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 8,
                      marginTop: 24,
                      paddingTop: 16,
                      borderTop: '1px solid #e1e3e5',
                    }}
                  >
                    <Button
                      disabled={section === 'buys'}
                      onClick={() => {
                        const order: SideSection[] = ['buys', 'gets', 'stacking', 'advanced'];
                        const i = order.indexOf(section);
                        if (i > 0) setSection(order[i - 1]);
                      }}
                    >
                      {t("Back")}
                    </Button>
                    <Button
                      variant="primary"
                      disabled={
                        (section === 'buys' && !canGoNextFromBuys) ||
                        (section === 'gets' && !canGoNextFromGets)
                      }
                      onClick={() => {
                        const order: SideSection[] = ['buys', 'gets', 'stacking', 'advanced'];
                        const i = order.indexOf(section);
                        if (i < order.length - 1) setSection(order[i + 1]);
                        else setMainStep(2);
                      }}
                    >
                      {t("Next")}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Summary")}
                </Text>
                <SummaryBlock
                  title={t("Basic information")}
                  lines={[
                    { label: name || 'Untitled campaign' },
                    {
                      label: t('Start: {{date}}', {
                        date: `${schedule.startDate}${schedule.setEndDate && schedule.endDate ? ` → ${schedule.endDate}` : ''}`,
                      }),
                    },
                  ]}
                />
                <SummaryBlock
                  title={t("Customer buys")}
                  lines={[
                    {
                      label: t('Applies to: {{scope}}', {
                        scope:
                          appliesTo === 'all'
                            ? t('All products')
                            : appliesTo === 'products'
                              ? t('{{count}} products', {
                                  count: selectedBuyProducts.length,
                                })
                              : appliesTo === 'collections'
                                ? t('Collections')
                                : t('Product tags'),
                      }),
                    },
                    {
                      label: isQuantity
                        ? t('Quantity · {{amount}}+', {
                            amount: thresholdLabel,
                          })
                        : t('Cart value · {{amount}}+', {
                            amount: thresholdLabel,
                          }),
                    },
                  ]}
                />
                <SummaryBlock
                  title={t("Customer gets")}
                  lines={
                    giftProducts.length
                      ? giftProducts.map((g) => ({ label: g.title }))
                      : [{ label: t('Click to add gifts'), muted: true }]
                  }
                  onClick={() => setSection('gets')}
                />
                <SummaryBlock
                  title={t("Priority & stacking")}
                  lines={[
                    {
                      label: t('Priority: #{{n}}', {
                        n: priority || '1',
                      }),
                    },
                    {
                      label: t("Don't combine: {{value}}", {
                        value: stopOnMatch ? t('Yes') : t('No'),
                      }),
                    },
                  ]}
                />
                <SummaryBlock
                  title={t("Advanced settings")}
                  lines={[
                    {
                      label: usageLimitEnabled
                        ? t('Usage limit: {{count}} total', {
                            count: totalUses || '—',
                          })
                        : t('Usage limit: OFF'),
                    },
                    {
                      label: t('Eligibility: {{value}}', {
                        value:
                          eligibility === 'all'
                            ? t('All customers')
                            : eligibility === 'segments'
                              ? t('Segments')
                              : eligibility === 'utm'
                                ? t('UTM link')
                                : locationCodes.length
                                  ? t('{{count}} locations', {
                                      count: locationCodes.length,
                                    })
                                  : t('Location'),
                      }),
                    },
                  ]}
                />
              </BlockStack>
            </Card>
          </div>
        )}
      </BlockStack>

      <LocationPickerModal
        open={locationPickerOpen}
        selectedCodes={locationCodes}
        onClose={() => setLocationPickerOpen(false)}
        onConfirm={(selection) => {
          setLocationCodes(selection.map((s) => s.code));
          setLocationLabels(
            Object.fromEntries(selection.map((s) => [s.code, s.name])),
          );
        }}
      />

      <CatalogPickerModal
        key={`catalog-browse-${browseSession}`}
        open={browseOpen}
        mode={
          browseKind === 'buy-collections' ? 'collections' : 'products'
        }
        title={
          browseKind === 'gift-products'
            ? 'Select gift products'
            : browseKind === 'buy-collections'
              ? 'Select collections'
              : browseKind === 'tag-products'
                ? browseTags.length
                  ? `Products tagged: ${browseTags.join(', ')}`
                  : 'Products matching tags'
                : 'Select products'
        }
        selectedIds={browseSelectedIds}
        tags={browseKind === 'tag-products' ? browseTags : undefined}
        onClose={() => setBrowseOpen(false)}
        onToggle={toggleCatalogItem}
        onToggleMany={toggleCatalogMany}
      />
    </Page>
  );
}

function SummaryBlock({
  title,
  lines,
  onClick,
}: {
  title: string;
  lines: Array<{ label: string; muted?: boolean; tone?: 'success' }>;
  onClick?: () => void;
}) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <InlineStack gap="200" blockAlign="center">
        <Badge tone="info">●</Badge>
        <Text as="span" fontWeight="semibold">
          {title}
        </Text>
      </InlineStack>
      <div
        style={{
          marginLeft: 8,
          marginTop: 8,
          padding: 10,
          borderRadius: 10,
          background: '#f6f6f7',
        }}
      >
        <BlockStack gap="100">
          {lines.map((l, i) => (
            <Text
              key={`${l.label}-${i}`}
              as="p"
              variant="bodySm"
              tone={l.tone === 'success' ? 'success' : l.muted ? 'subdued' : undefined}
            >
              {l.label}
            </Text>
          ))}
        </BlockStack>
      </div>
    </div>
  );
}
